import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { admin, api } from '../api/client'
import type {
  Alarm, AlertCondition, AlertRule, AlertRuleOptions, NotificationRecipients,
  NotificationSettings, OutboxEmail,
} from '../api/types'
import { useAsync } from '../lib/useAsync'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/time'
import {
  CONDITIONS, type Draft, alarmLink, alarmWhere, alarmsChecked, conditionLabel, draftFrom, emptyDraft,
  hasHold, levelHint, levelUnit, ruleSummary, toBody,
} from '../lib/alerts'
import { AlarmTestPanel } from '../components/AlarmTestPanel'
import { AlarmLab, InlineFormulaEditor } from '../components/AlarmLab'
import { AlarmTiming, alarmTimingSummary } from '../components/AlarmTiming'
import { RuleRevisions } from '../components/RuleRevisions'
import { NotificationEscalation } from '../components/NotificationEscalation'
import './alarm-workspace.css'

const message = (failed: unknown) => failed instanceof Error ? failed.message : 'Something went wrong.'
const DEMO = import.meta.env.VITE_DEMO_MODE === 'true'
type AlarmTab = 'rules' | 'events' | 'notifications' | 'lab'
const editableRule = (rule: AlertRule): Draft => ({ ...draftFrom(rule), holdMin: String(rule.hold_s / 60) })

const TEMPLATES: { condition: AlertCondition; title: string; detail: string; glyph: string; scope: Draft['scope'] }[] = [
  { condition: 'above', title: 'High threshold', detail: 'At or above a limit', glyph: '≥', scope: 'reading' },
  { condition: 'below', title: 'Low threshold', detail: 'At or below a limit', glyph: '≤', scope: 'reading' },
  { condition: 'rate', title: 'Rate of change', detail: 'Absolute change per day', glyph: 'Δ', scope: 'reading' },
  { condition: 'stale', title: 'Missing readings', detail: 'Minutes since a reading', glyph: 't', scope: 'reading' },
  { condition: 'nodata', title: 'Station silence', detail: 'Expected intervals missed', glyph: '∅', scope: 'station' },
  { condition: 'gateway', title: 'Gateway offline', detail: 'Hours without a response', glyph: '↔', scope: 'station' },
  { condition: 'anyfast', title: 'Group movement', detail: 'Fastest channel in a group', glyph: 'Σ', scope: 'station' },
]

/** Rule setup and active events are separate views. Every production write
 *  still passes through the existing role-aware API. */
export function Alerts() {
  const { user } = useAuth()
  const platformAdmin = user?.role === 'admin'
  const [params, setParams] = useSearchParams()
  const project = Number(params.get('project')) || null
  const requestedTab = params.get('tab')
  const tab: AlarmTab = requestedTab === 'lab' || requestedTab === 'events' || (requestedTab === 'notifications' && platformAdmin) ? requestedTab : 'rules'
  const setTab = (value: AlarmTab) => {
    const next = new URLSearchParams(params)
    next.set('tab', value); next.delete('new'); setParams(next)
  }
  const [scope, setScope] = useState<'all' | 'reading' | 'station'>('all')
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [startFormula, setStartFormula] = useState(false)
  const [saved, setSaved] = useState(false)
  const [notice, setNotice] = useState('')
  const [toggleError, setToggleError] = useState<string>()
  const [toggling, setToggling] = useState<number | null>(null)
  const newButton = useRef<HTMLButtonElement>(null)
  const rules = useAsync(() => api.alerts.rules(project), [project])
  const stations = useAsync(() => api.alerts.stations(), [])
  const projects = useAsync(() => api.projects.list(), [])
  const mail = useAsync(() => api.alerts.mailConfigured(), [])
  const events = useAsync(() => api.alerts.alarms(project ? { project } : {}), [project])
  const all = rules.data?.rules ?? []
  const active = events.data?.alarms ?? []
  const stationList = stations.data?.stations ?? []
  const canSet = platformAdmin || stationList.length > 0
  const searched = query.trim().toLocaleLowerCase()
  const shown = all.filter((rule) => (scope === 'all' || rule.scope === scope)
    && (!searched || [rule.name, rule.station.name, rule.parameter?.name, conditionLabel(rule.condition)]
      .join(' ').toLocaleLowerCase().includes(searched)))
  const shownEvents = active.filter((event) => !searched || [event.rule.name, alarmWhere(event), event.parameter?.name]
    .join(' ').toLocaleLowerCase().includes(searched))
  const fresh = alarmsChecked(events.data?.checked_at)
  const edit = (next: Draft | null) => { setStartFormula(false); setSaved(false); setNotice(''); setDraft(next) }
  const close = () => { setDraft(null); newButton.current?.focus() }
  const refresh = () => { rules.reload(); events.reload() }
  const chooseProject = (value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set('project', value); else next.delete('project')
    setParams(next)
  }
  const newFrom = (condition: AlertCondition = 'above') => {
    setStartFormula(false)
    const template = TEMPLATES.find((item) => item.condition === condition)!
    const station = condition === 'gateway'
      ? stationList.find((item) => item.vendor === 'cmt_edge') : stationList[0]
    edit({ ...emptyDraft(station?.code ?? ''), scope: template.scope, condition })
  }
  const newFormula = (source?: string, name?: string) => {
    edit({ ...emptyDraft(source?.split('|')[0] ?? stationList[0]?.code ?? ''), name: name ?? '' })
    setStartFormula(true)
  }
  const toggle = async (rule: AlertRule) => {
    setToggling(rule.id)
    try {
      setToggleError(undefined)
      await api.alerts.update(rule.id, { enabled: !rule.enabled })
      refresh()
    } catch (failed) { setToggleError(message(failed)) }
    finally { setToggling(null) }
  }

  return <div className="page aw-page">
    <header className="aw-page-head">
      <h1 className="sr-only">Alarms</h1>
      <div className="aw-heading-actions">
        <button type="button" className="button aw-refresh" onClick={refresh}>Refresh</button>
        {canSet && tab !== 'lab' && <button ref={newButton} type="button" className="button primary" onClick={() => newFrom()}>
          <span aria-hidden="true">+</span> New rule
        </button>}
      </div>
    </header>

    <div className="aw-metrics" aria-label="Alarm summary">
      <div><span>Configured rules</span><strong>{rules.data ? all.length : '…'}</strong></div>
      <div><span>Enabled</span><strong>{rules.data ? all.filter((rule) => rule.enabled).length : '…'}</strong></div>
      <div className="aw-metric-alarm"><span>Open alarms</span><strong>{events.data ? active.filter((event) => event.level === 'alarm').length : '…'}</strong></div>
      <div className="aw-metric-warning"><span>Warnings</span><strong>{events.data ? active.filter((event) => event.level === 'warning').length : '…'}</strong></div>
    </div>

    <div className="aw-tabbar">
      <div className="aw-tabs" role="tablist" aria-label="Alarm workspace" onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')]
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement)
        if (index < 0) return
        event.preventDefault()
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
        buttons[next]?.focus(); buttons[next]?.click()
      }}>
        <button type="button" role="tab" id="aw-tab-rules" aria-controls="aw-panel-rules" aria-selected={tab === 'rules'}
          tabIndex={tab === 'rules' ? 0 : -1}
          onClick={() => setTab('rules')}>Rules <span>{all.length}</span></button>
        <button type="button" role="tab" id="aw-tab-events" aria-controls="aw-panel-events" aria-selected={tab === 'events'}
          tabIndex={tab === 'events' ? 0 : -1}
          onClick={() => setTab('events')}>Active events <span>{active.length}</span></button>
        {platformAdmin && <button type="button" role="tab" id="aw-tab-notifications" aria-controls="aw-panel-notifications"
          tabIndex={tab === 'notifications' ? 0 : -1}
          aria-selected={tab === 'notifications'} onClick={() => setTab('notifications')}>Notifications</button>}
        <button type="button" role="tab" id="aw-tab-lab" aria-controls="aw-panel-lab" aria-selected={tab === 'lab'}
          tabIndex={tab === 'lab' ? 0 : -1} onClick={() => setTab('lab')}>Alarm Lab</button>
      </div>
      {DEMO ? <span className="aw-scope-note">Local demo rules · No delivery</span>
        : mail.data && !mail.data.configured ? <span className="aw-scope-note">Email not configured</span> : null}
    </div>

    {notice && <p className="aw-feedback" role="status">{notice}</p>}
    {toggleError && <p className="error" role="alert">{toggleError}</p>}
    {tab !== 'notifications' && tab !== 'lab' && <div className="aw-filters">
      <label className="aw-search"><span className="visually-hidden">Search rules or events</span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>
        <input type="search" placeholder={tab === 'rules' ? 'Search rules, stations or sensors' : 'Search active events'}
          value={query} onChange={(event) => setQuery(event.target.value)} />
      </label>
      <select aria-label="Filter alarms by project" value={project ?? ''} onChange={(event) => chooseProject(event.target.value)}>
        <option value="">All projects</option>
        {(projects.data?.projects ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      {tab === 'rules' && <select aria-label="Rule scope" value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}>
        <option value="all">All rule types</option><option value="reading">Sensor rules</option><option value="station">Station rules</option>
      </select>}
    </div>}

    {tab === 'rules' && <section id="aw-panel-rules" role="tabpanel" aria-labelledby="aw-tab-rules" className="aw-panel">
      {rules.error && <p className="error aw-pad">{rules.error}</p>}
      {stations.error && <p className="error aw-pad">{stations.error}</p>}
      {!stations.loading && !stations.error && !canSet && <p className="aw-pad muted">A project Editor or Admin can configure rules. Active events are available in the next tab.</p>}
      {canSet && <RuleTable rules={shown} loading={rules.loading} toggling={toggling}
        onPick={(rule) => edit(editableRule(rule))} onToggle={toggle}
        onDuplicate={(rule) => edit({ ...editableRule(rule), id: undefined, name: (rule.name + ' copy').slice(0, 80) })} />}
    </section>}

    {tab === 'rules' && canSet && <section className="aw-template-section" aria-labelledby="aw-template-head">
      <div className="aw-section-title"><h2 id="aw-template-head">Start with a rule type</h2><span>Sensor and station conditions</span></div>
      <div className="aw-templates">
        <button type="button" className="aw-template" onClick={() => newFormula()}><span className="aw-template-symbol" aria-hidden="true">ƒ</span><span><strong>Formula rule</strong><small>Channel expressions and replay</small></span><span className="aw-template-arrow" aria-hidden="true">↗</span></button>
        {TEMPLATES.filter((item) => item.condition !== 'gateway' || stationList.some((station) => station.vendor === 'cmt_edge'))
          .map((item) => <button type="button" key={item.condition} className="aw-template" onClick={() => newFrom(item.condition)}>
            <span className="aw-template-symbol" aria-hidden="true">{item.glyph}</span>
            <span><strong>{item.title}</strong><small>{item.detail}</small></span>
            <span className="aw-template-arrow" aria-hidden="true">↗</span>
          </button>)}
      </div>
    </section>}

    {tab === 'events' && <section id="aw-panel-events" role="tabpanel" aria-labelledby="aw-tab-events" className="aw-panel">
      <div className="aw-event-check">
        <span>{events.data?.checked_at ? 'Checked ' + formatDateTime(events.data.checked_at) + ' · Tbilisi' : 'Monitor check unavailable'}</span>
        {!events.loading && !fresh && <span className="aw-check-unknown">Check delayed</span>}
      </div>
      {events.error && <p className="error aw-pad">{events.error}</p>}
      <EventTable events={shownEvents} loading={events.loading} fresh={fresh} filtered={Boolean(searched)}
        canEdit={(event) => all.some((rule) => rule.id === event.rule.id)}
        onEdit={(event) => { const rule = all.find((item) => item.id === event.rule.id); if (rule) edit(editableRule(rule)) }} />
    </section>}

    {tab === 'notifications' && platformAdmin && <div id="aw-panel-notifications" role="tabpanel" aria-labelledby="aw-tab-notifications">
      <Notifications />
    </div>}

    {tab === 'lab' && <AlarmLab />}

    {draft && <RuleEditor key={draft.id ?? 'new'} draft={draft} setDraft={setDraft} saved={saved} stations={stationList}
      onSaved={(rule) => { setSaved(true); setDraft(editableRule(rule)); refresh() }}
      onDeleted={() => { close(); setNotice(DEMO ? 'Local demo rule deleted.' : 'Rule deleted.'); refresh() }}
      onCancel={close} initialFormula={startFormula} project={project}
      onFormulaSaved={() => { close(); setNotice('Formula rule saved, switched off. Open Alarm Lab to review and enable it.'); refresh() }} />}
  </div>
}

function RuleTable({ rules, loading, toggling, onPick, onToggle, onDuplicate }: {
  rules: AlertRule[]; loading: boolean; toggling: number | null
  onPick: (rule: AlertRule) => void; onToggle: (rule: AlertRule) => void; onDuplicate: (rule: AlertRule) => void
}) {
  return <div className="aw-table-scroll"><table className="aw-table">
    <thead><tr><th>Rule / Source</th><th>Condition</th><th>Levels</th><th>Recipients</th><th>Enabled</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
    <tbody>{rules.map((rule) => {
      const summary = ruleSummary(rule)
      const comparator = rule.condition === 'below' ? '≤' : '≥'
      const hold = rule.hold_s % 60 ? rule.hold_s + ' s' : (rule.hold_s / 60) + ' min'
      return <tr key={rule.id}>
        <td><button type="button" className="aw-rule-name" aria-label={'Edit ' + rule.name} onClick={() => onPick(rule)}>{rule.name}</button>
          <small>{rule.station.name}{rule.parameter ? ' · ' + rule.parameter.name : rule.axis_group ? ' · ' + rule.axis_group : ''}</small></td>
        <td><span className="aw-condition">{TEMPLATES.find((item) => item.condition === rule.condition)?.title ?? summary.condition}</span>
          <small>{rule.scope === 'reading' ? 'Sensor rule' : 'Station rule'}{rule.hold_s ? ' · ' + hold + ' hold' : ''}</small></td>
        <td><div className="aw-rule-levels">{summary.warn && <span className="aw-warning-level">Warning <b>{comparator} {summary.warn}</b></span>}
          <span className="aw-alarm-level">Alarm <b>{comparator} {summary.alarm}</b></span></div>
          {rule.open && <span className={'aw-event-state aw-event-' + rule.open.level}>{rule.open.level === 'alarm' ? 'Active alarm' : 'Active warning'}</span>}</td>
        <td><span className="aw-recipient">{summary.tells === 'Nobody' ? 'None selected' : summary.tells}</span></td>
        <td><label className="aw-switch"><input type="checkbox" checked={rule.enabled} disabled={toggling !== null}
          aria-label={(rule.enabled ? 'Switch off ' : 'Switch on ') + rule.name} onChange={() => onToggle(rule)} /><span aria-hidden="true" /></label></td>
        <td><button type="button" className="aw-icon-action" title="Duplicate rule" aria-label={'Duplicate ' + rule.name} onClick={() => onDuplicate(rule)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V4H4v12h4" /></svg>
        </button></td>
      </tr>
    })}</tbody>
  </table>
  {!rules.length && <div className="aw-empty"><strong>{loading ? 'Loading rules…' : 'No matching rules'}</strong>
    {!loading && <span>Create a rule or adjust the filters.</span>}</div>}
  </div>
}

function EventTable({ events, loading, fresh, filtered, canEdit, onEdit }: {
  events: Alarm[]; loading: boolean; fresh: boolean; filtered: boolean; canEdit: (event: Alarm) => boolean; onEdit: (event: Alarm) => void
}) {
  return <div className="aw-event-list">
    {events.map((event) => <article className="aw-event-card" key={event.id}>
      <span className={'aw-event-symbol aw-event-' + event.level} aria-hidden="true">!</span>
      <div className="aw-event-content"><span className={'aw-event-state aw-event-' + event.level}>{event.level === 'alarm' ? 'Alarm' : 'Warning'}</span>
        <h3>{event.rule.name}</h3><p>{alarmWhere(event)}{event.parameter ? ' · ' + event.parameter.name : ''}</p>
        {event.rule.message && <p className="aw-event-message">{event.rule.message}</p>}
        <small>Opened {formatDateTime(event.since)}{event.escalated_at ? ' · Escalated ' + formatDateTime(event.escalated_at) : ''}</small>
      </div>
      <div className="aw-event-actions"><Link className="button" to={alarmLink(event)}>{event.station ? 'View readings' : 'Open strategy'}</Link>
        {canEdit(event) && <button type="button" className="button" onClick={() => onEdit(event)}>Edit rule</button>}</div>
    </article>)}
    {!events.length && <div className="aw-empty"><strong>{loading ? 'Loading active events…' : filtered ? 'No matching active events' : fresh ? 'No active events' : 'Event status unavailable'}</strong>
      {!loading && !fresh && <span>The monitor check is delayed. Refresh to check again.</span>}</div>}
  </div>
}

export function RuleEditor({ draft, setDraft, saved, stations, onSaved, onDeleted, onCancel, initialFormula = false, project = null, onFormulaSaved }: {
  draft: Draft; setDraft: (next: Draft) => void; saved: boolean
  stations: { code: string; name: string; vendor: string }[]
  onSaved: (rule: AlertRule) => void; onDeleted: () => void; onCancel: () => void
  initialFormula?: boolean; project?: number | null; onFormulaSaved: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { const element = dialog.current; element?.showModal(); return () => element?.close() }, [])
  const [formula, setFormula] = useState(initialFormula)
  const [formulaOpened, setFormulaOpened] = useState(initialFormula)
  const options = useAsync<AlertRuleOptions | undefined>(() => draft.station ? api.alerts.options(draft.station) : Promise.resolve(undefined), [draft.station])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [changedSinceSave, setChangedSinceSave] = useState(false)
  const [windowEnabled, setWindowEnabled] = useState(Number(draft.holdMin) > 0)
  // A rule is changed only while it is off (spec 7.3, the server's rule: any
  // patch on a rule that is on but its switch is refused). Switching off is
  // itself a recorded change, and it closes the open alarm with no email.
  const [live, setLive] = useState(draft.id !== undefined && draft.enabled)
  const [revision, setRevision] = useState(0)
  const locked = live
  const switchOff = async () => {
    if (draft.id === undefined) return
    setBusy(true); setError(undefined)
    try {
      const result = await api.alerts.update(draft.id, { enabled: false })
      setDraft({ ...draft, enabled: false }); setLive(false); setRevision(n => n + 1); onSaved(result.rule)
    } catch (failed) { setError(message(failed)) }
    finally { setBusy(false) }
  }
  const set = (patch: Partial<Draft>) => { setError(undefined); setChangedSinceSave(true); setDraft({ ...draft, ...patch }) }
  useEffect(() => { if (saved) setChangedSinceSave(false) }, [saved])
  const opts = options.data?.station.code === draft.station ? options.data : undefined
  const parameters = opts?.parameters ?? []
  const parameter = draft.parameter || parameters[0]?.code || ''
  const group = draft.axis_group || opts?.groups[0] || ''
  const paramUnit = draft.scope === 'reading' ? parameters.find((item) => item.code === parameter)?.unit
    : parameters.find((item) => item.axis_group === group)?.unit
  const unit = levelUnit(draft.condition, paramUnit)
  const hint = levelHint(draft.condition, draft.warn, draft.alarm)
  const stationName = stations.find((station) => station.code === draft.station)?.name ?? draft.station
  const readingName = parameters.find((item) => item.code === parameter)?.name
  const suggested = ((draft.scope === 'reading' && readingName ? readingName : stationName) + ': ' + conditionLabel(draft.condition)).slice(0, 80)
  const isGateway = opts?.station.vendor === 'cmt_edge'
  const conditions = TEMPLATES.filter((item) => item.scope === draft.scope && (item.condition !== 'gateway' || isGateway))
  const noReading = draft.scope === 'reading' && opts !== undefined && parameters.length === 0
  const finiteLevels = Number.isFinite(Number(draft.alarm)) && (draft.warn.trim() === '' || Number.isFinite(Number(draft.warn)))
  const positiveLevels = !['stale', 'nodata', 'gateway'].includes(draft.condition)
    || (Number(draft.alarm) > 0 && (draft.warn.trim() === '' || Number(draft.warn) > 0))
  const validHold = !hasHold(draft.condition) || (draft.holdMin.trim() !== '' && Number.isFinite(Number(draft.holdMin))
    && Number(draft.holdMin) >= 0 && Number(draft.holdMin) <= 1440)
  const valid = !hint.bad && finiteLevels && positiveLevels && validHold && !noReading && Boolean(draft.station && opts)
    && conditions.some((item) => item.condition === draft.condition) && (draft.condition !== 'anyfast' || Boolean(group))
    && (draft.scope !== 'reading' || parameters.some((item) => item.code === parameter))
  const body = toBody({ ...draft, parameter, axis_group: group, name: draft.name.trim() || suggested })
  const save = async () => {
    if (!valid || busy) return
    setBusy(true); setError(undefined)
    try {
      const result = draft.id === undefined ? await api.alerts.create(body) : await api.alerts.update(draft.id, body)
      setChangedSinceSave(false); setLive(result.rule.enabled); setRevision(n => n + 1); onSaved(result.rule)
    } catch (failed) { setError(message(failed)) }
    finally { setBusy(false) }
  }
  const remove = async () => {
    if (draft.id === undefined) return
    setBusy(true); setError(undefined)
    try { await api.alerts.remove(draft.id); onDeleted() }
    catch (failed) { setError(message(failed)); setBusy(false) }
  }
  const togglePick = <T,>(list: T[], item: T) => list.includes(item) ? list.filter((value) => value !== item) : [...list, item]

  return <dialog ref={dialog} className={`aw-dialog${formula ? ' alab-dialog aw-formula-dialog' : ''}`} aria-labelledby="aw-editor-title" onCancel={(event) => { event.preventDefault(); if (!busy) onCancel() }}>
    <header className="aw-dialog-head">
      <div><span className="aw-eyebrow">RULE CONFIGURATION</span><h2 id="aw-editor-title">{draft.id === undefined ? 'New alarm rule' : 'Edit alarm rule'}</h2></div>
      <button type="button" className="aw-close" aria-label="Close rule editor" disabled={busy} onClick={onCancel}>×</button>
    </header>
    {formulaOpened && <div className="aw-formula-content" hidden={!formula}>
      <div className="aw-formula-switch"><button type="button" className="button" onClick={() => setFormula(false)}>← Sensor / station rule</button><span>Formula rule</span></div>
      <InlineFormulaEditor source={parameter && draft.station ? `${draft.station}|${parameter}` : undefined} name={draft.name} project={project} onClose={onCancel} onSaved={onFormulaSaved} />
    </div>}
    <div className="aw-editor-body" hidden={formula}>
      {locked && <div className="aw-locked" role="status">
        <p><strong>This rule is on.</strong> Switch it off to change it. This closes its open alarm with no email. Switching it back on raises a new one if the condition still holds.</p>
        <button type="button" className="button" disabled={busy} onClick={() => void switchOff()}>Switch off to edit</button>
      </div>}
      {saved && !changedSinceSave && <p className="aw-feedback" role="status">{DEMO ? 'Saved as a local demo rule. No event or email is created.' : 'Rule saved.'}</p>}
      {options.error && <p className="error" role="alert">{options.error}</p>}
      <fieldset className="aw-editor-fields" disabled={locked}>
      <label className="field aw-rule-title"><span>Rule name</span><input type="text" value={draft.name} maxLength={80}
        placeholder={suggested} onChange={(event) => set({ name: event.target.value })} /></label>
      <div className="aw-editor-section">
        <div className="aw-editor-section-title"><span>01</span><h3>Source and condition</h3></div>
        <div className="aw-source-scope" role="group" aria-label="Rule source">
          {(['reading', 'station'] as const).map((value) => <button type="button" key={value} aria-pressed={draft.scope === value}
            onClick={() => { setWindowEnabled(false); set({ scope: value, condition: CONDITIONS[value][0]![0], holdMin: '0' }) }}>{value === 'reading' ? 'Sensor reading' : 'Whole station'}</button>)}
          {draft.id === undefined && <button type="button" aria-pressed={false} disabled={busy} onClick={() => { setFormulaOpened(true); setFormula(true) }}>Formula rule</button>}
        </div>
        <div className="al-grid">
          <label className="field"><span>Station</span><select value={draft.station}
            onChange={(event) => {
              const station = stations.find((item) => item.code === event.target.value)
              set({ station: event.target.value, parameter: '', axis_group: '', users: [], projects: [],
                condition: draft.condition === 'gateway' && station?.vendor !== 'cmt_edge' ? 'nodata' : draft.condition })
            }}>{stations.map((station) => <option key={station.code} value={station.code}>{station.name}</option>)}</select></label>
          {draft.scope === 'reading' && <label className="field"><span>Sensor channel</span><select value={parameter} disabled={!parameters.length}
            onChange={(event) => set({ parameter: event.target.value })}>
            {parameters.map((item) => <option key={item.code} value={item.code}>{item.name}{item.unit ? ' (' + item.unit + ')' : ''}</option>)}
          </select></label>}
          {draft.condition === 'anyfast' && <label className="field"><span>Quantity group</span><select value={group}
            onChange={(event) => set({ axis_group: event.target.value })}>{(opts?.groups ?? []).map((item) => <option key={item} value={item}>{item}</option>)}</select></label>}
        </div>
        {noReading && <p className="error">This station has no available readings.</p>}
        <div className="aw-condition-pick" role="group" aria-label="Alarm condition">
          {conditions.map((item) => <button key={item.condition} type="button" aria-pressed={draft.condition === item.condition}
            onClick={() => { if (!hasHold(item.condition)) setWindowEnabled(false); set({ condition: item.condition, holdMin: hasHold(item.condition) ? draft.holdMin : '0' }) }}>
            <span aria-hidden="true">{item.glyph}</span>{item.title}
          </button>)}
        </div>
      </div>

      <div className="aw-editor-section">
        <div className="aw-editor-section-title"><span>02</span><h3>Trigger levels</h3><small>{unit}</small></div>
        <div className="al-grid al-levels-grid">
          <label className="field al-level-warn"><span>Warning</span><span className="al-unit-input">
            <input type="number" step="any" value={draft.warn} placeholder="Optional" aria-label="Warning level"
              onChange={(event) => set({ warn: event.target.value })} /><span>{unit}</span></span></label>
          <label className="field al-level-alarm"><span>Alarm</span><span className={'al-unit-input' + (hint.bad ? ' is-bad' : '')}>
            <input type="number" step="any" value={draft.alarm} aria-label="Alarm level" aria-required="true" aria-invalid={hint.bad}
              onChange={(event) => set({ alarm: event.target.value })} /><span>{unit}</span></span></label>
        </div>
        {hint.bad && draft.alarm.trim() !== '' && <span className="error small">{hint.text}</span>}
        {!finiteLevels && <span className="error small">Enter finite numeric levels.</span>}
        {!positiveLevels && draft.alarm.trim() !== '' && <span className="error small">Time and interval levels must be greater than zero.</span>}
        {hasHold(draft.condition) && <details className="alarm-evaluation" open={draft.id !== undefined && Number(draft.holdMin) > 0 || undefined}>
          <summary>Evaluation options <small>{windowEnabled ? `${draft.holdMin || '…'} min window` : alarmTimingSummary('first', 0)}</small></summary>
          <AlarmTiming mode={windowEnabled ? 'sustained' : 'first'} minutes={draft.holdMin} windowed
            onMode={mode => { setWindowEnabled(mode === 'sustained'); set({ holdMin: mode === 'first' ? '0' : Number(draft.holdMin) > 0 ? draft.holdMin : '1' }) }}
            onMinutes={holdMin => set({ holdMin })} />
          {!validHold && <span className="error small">Enter a duration between 0 and 1,440 minutes.</span>}
        </details>}
        {draft.scope === 'reading' && <AlarmTestPanel rule={body} unit={paramUnit ?? ''} valid={valid} />}
      </div>

      <details className="aw-editor-fold">
        <summary><span className="aw-step">03</span>Recipients and message <small>{draft.users.length + draft.projects.length} selected</small></summary>
        <div className="aw-recipient-picks">
          <label className="field"><span>Alarm message</span><textarea rows={2} value={draft.message} maxLength={500}
            placeholder="Action or context for the operator" onChange={(event) => set({ message: event.target.value })} /></label>
          {(opts?.projects ?? []).map((item) => <label key={'p' + item.id} className="aw-recipient-option">
            <input type="checkbox" checked={draft.projects.includes(item.id)} onChange={() => set({ projects: togglePick(draft.projects, item.id) })} />
            <span>{item.name}<small>Project members</small></span>
          </label>)}
          {(opts?.people ?? []).map((item) => <label key={'u' + item.id} className="aw-recipient-option">
            <input type="checkbox" checked={draft.users.includes(item.id)} onChange={() => set({ users: togglePick(draft.users, item.id) })} />
            <span>{item.name}<small>{item.email}</small></span>
          </label>)}
          <div className="al-when"><span>Notify on</span>{(['warning', 'alarm', 'cleared'] as const).map((value) => <label key={value}>
            <input type="checkbox" checked={draft.notify_on.includes(value)} onChange={() => set({ notify_on: togglePick(draft.notify_on, value) })} />
            {{ warning: 'Warning', alarm: 'Alarm', cleared: 'Cleared' }[value]}
          </label>)}</div>
          {DEMO && <small className="aw-scope-note">Recipients are configuration only in this demonstration.</small>}
        </div>
      </details>
      <label className="aw-enable"><input type="checkbox" checked={draft.enabled} onChange={() => set({ enabled: !draft.enabled })} /><span>Rule enabled</span></label>
      </fieldset>
      {error && <p className="error" role="alert">{error}</p>}
      {draft.id !== undefined && <RuleRevisions load={() => api.alerts.history(draft.id!)} refresh={`${draft.id}:${revision}`} />}
    </div>
    <footer className="aw-dialog-actions" hidden={formula}>
      {draft.id !== undefined && (confirmDelete ? <div className="aw-delete-confirm">
        <button type="button" className="button danger" disabled={busy} onClick={remove}>Confirm delete</button>
        <button type="button" className="button" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep rule</button>
      </div> : <button type="button" className="aw-delete" disabled={busy || locked} title={locked ? 'Switch the rule off to delete it' : undefined} onClick={() => setConfirmDelete(true)}>Delete rule</button>)}
      <span className="al-spacer" />
      <button type="button" className="button" disabled={busy} onClick={onCancel}>{saved && !changedSinceSave ? 'Done' : 'Cancel'}</button>
      <button type="button" className="button primary" disabled={!valid || busy || locked} onClick={save}>{busy ? 'Saving…' : 'Save rule'}</button>
    </footer>
  </dialog>
}

const REMIND = [0, 1, 4, 12, 24] as const

/** How each person is told, and delivery (platform admins). */
function Notifications() {
  const recipients = useAsync<NotificationRecipients>(() => admin.recipients(), [])
  const settings = useAsync<NotificationSettings>(() => admin.notificationSettings(), [])
  const failed = useAsync(() => Promise.all([admin.outbox('failed'), admin.outbox('skipped')]), [])
  const [error, setError] = useState<string>()
  const [people, setPeople] = useState<NotificationRecipients['people']>([])
  const [delivery, setDelivery] = useState<NotificationSettings | null>(null)
  useEffect(() => { if (recipients.data) setPeople(recipients.data.people) }, [recipients.data])
  useEffect(() => { if (settings.data) setDelivery(settings.data) }, [settings.data])
  const emails = useMemo<OutboxEmail[]>(() => (failed.data ?? [])
    .flatMap((r) => r.emails).sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 50),
  [failed.data])

  const setPerson = async (id: number, patch: { email_on?: boolean; receives?: 'both' | 'alarm' }) => {
    const person = people.find((p) => p.id === id)
    if (!person) return
    const next = { ...person, ...patch }
    setPeople(people.map((p) => (p.id === id ? next : p)))
    try {
      setError(undefined)
      await admin.setPref(id, { email: next.email_on, receives: next.receives })
    } catch (failure) {
      setError(message(failure)); recipients.reload()
    }
  }
  const setSettings = async (next: NotificationSettings) => {
    setDelivery(next)
    try {
      setError(undefined)
      setDelivery(await admin.setNotificationSettings(next))
    } catch (failure) {
      setError(message(failure)); settings.reload()
    }
  }

  return (
    <section className="al-notifications" aria-labelledby="al-notif-head">
      <div className="al-section-head al-editor-head">
        <h2 id="al-notif-head">Notifications</h2>
        <span className="muted">
          How each recipient is told, for every rule that names them. Only hydrodiagnostics.ge
          accounts, by email.
        </span>
      </div>
      <div className="al-notif-body">
        {(recipients.error || settings.error) && <p className="error" role="alert">{recipients.error ?? settings.error}</p>}
        <div className="al-scroll">
          <div className="al-people">
            <div className="al-person al-head"><span>Recipient</span><span>Email</span><span>Receives</span></div>
            {people.map((p) => (
              <div key={p.id} className="al-person">
                <span className="al-cell">
                  <span className="al-name">{p.name}</span>
                  <span className="st-sub">{p.email} · {p.role === 'admin' ? 'Admin' : 'Viewer'}</span>
                </span>
                <span>
                  <input type="checkbox" checked={p.email_on} aria-label={`Email ${p.name}`}
                         onChange={() => setPerson(p.id, { email_on: !p.email_on })} />
                </span>
                <span>
                  <select value={p.receives} aria-label={`What ${p.name} receives`}
                          onChange={(e) => setPerson(p.id, { receives: e.target.value as 'both' | 'alarm' })}>
                    <option value="both">Warnings and alarms</option>
                    <option value="alarm">Alarms only</option>
                  </select>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="al-delivery">
          <div className="al-delivery-head">Delivery</div>
          {delivery && (
            <>
              <label>
                While an alarm stays open, remind every{' '}
                <select value={delivery.remind_every_h}
                        onChange={(e) => setSettings({ ...delivery,
                          remind_every_h: Number(e.target.value) as NotificationSettings['remind_every_h'] })}>
                  {REMIND.map((h) => <option key={h} value={h}>{h === 0 ? 'Never' : `${h} h`}</option>)}
                </select>
              </label>
              <label>
                <input type="checkbox" className="switch" checked={delivery.digest}
                       onChange={() => setSettings({ ...delivery, digest: !delivery.digest })} />
                Daily summary of open alerts at <span className="mono">09:00</span> Tbilisi
              </label>
              <p className="muted">
                {delivery.remind_every_h === 0 ? 'One message when an alert opens.'
                  : `One message when an alert opens, then a reminder every ${delivery.remind_every_h} h until it clears.`}
              </p>
            </>
          )}
          {error && <p className="error" role="alert">{error}</p>}
        </div>
      </div>
      <details className="fold al-recent">
        <summary><h3>Recent emails not sent</h3> <span className="muted">({emails.length})</span></summary>
        {failed.error && <p className="error" role="alert">{failed.error}</p>}
        {emails.length ? (
          <table className="grid">
            <thead><tr><th>When</th><th>To</th><th>Subject</th><th>Why</th></tr></thead>
            <tbody>
              {emails.map((m) => (
                <tr key={m.id}>
                  <td className="mono">{formatDateTime(m.created_at)}</td>
                  <td>{m.to_email}</td>
                  <td>{m.subject}</td>
                  <td>{m.status === 'skipped' ? 'Not sent: ' : 'Failed: '}{m.last_error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="muted">{failed.loading ? 'Loading delivery history…' : failed.error ? 'Delivery history unavailable.' : 'No failed or skipped emails in recent history.'}</p>}
      </details>
      <NotificationEscalation people={people.map((p) => ({ id: p.id, name: p.name }))} />
    </section>
  )
}
